<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { formatNumber, formatPercent } from '../helpers';

const props = defineProps({
  metrics: { type: Object, required: true },
});
const { t, locale } = useI18n();

const na = computed(() => t('WASAPI_CAMPAIGNS.DETAIL.METRICS.NOT_AVAILABLE'));
const num = v => formatNumber(v, locale.value);
const pct = v => formatPercent(v, locale.value);

const cards = computed(() => {
  const m = props.metrics;
  const readAvailable = m.read_available !== false;
  return [
    { key: 'AUDIENCE', value: num(m.audience), sub: null },
    { key: 'SENT', value: num(m.sent), sub: null, tone: 'blue' },
    {
      key: 'DELIVERED',
      value: num(m.delivered),
      sub: pct(m.rates?.delivery),
      tone: 'teal',
    },
    {
      key: 'READ',
      value: readAvailable ? num(m.read) : na.value,
      sub: readAvailable
        ? pct(m.rates?.read)
        : t('WASAPI_CAMPAIGNS.DETAIL.METRICS.NOT_AVAILABLE_HINT'),
      tone: readAvailable ? 'teal' : 'muted',
    },
    {
      key: 'FAILED',
      value: num(m.failed),
      sub: pct(m.rates?.failure),
      tone: 'ruby',
    },
    { key: 'PENDING', value: num(m.pending), sub: null },
    { key: 'SKIPPED', value: num(m.skipped), sub: null, tone: 'muted' },
    {
      key: 'OPT_OUTS',
      value: num(m.opt_outs),
      sub: pct(m.rates?.opt_out),
      tone: 'amber',
    },
  ];
});

const TONE = {
  blue: 'text-n-blue-11',
  teal: 'text-n-teal-11',
  ruby: 'text-n-ruby-11',
  amber: 'text-n-amber-11',
  muted: 'text-n-slate-10',
};
</script>

<template>
  <div class="grid grid-cols-2 md:grid-cols-4 gap-3">
    <div
      v-for="card in cards"
      :key="card.key"
      class="flex flex-col gap-1 p-4 rounded-xl outline outline-1 outline-n-weak bg-n-solid-1"
    >
      <span class="text-xs uppercase tracking-wide text-n-slate-11">
        {{ t(`WASAPI_CAMPAIGNS.DETAIL.METRICS.${card.key}`) }}
      </span>
      <span
        class="text-2xl font-semibold tabular-nums"
        :class="TONE[card.tone] || 'text-n-slate-12'"
      >
        {{ card.value }}
      </span>
      <span v-if="card.sub" class="text-xs text-n-slate-11">{{
        card.sub
      }}</span>
    </div>
  </div>
</template>
